import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Trophy } from "lucide-react";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";

// Matches get-public-landing edge function response for league
interface LeagueEntity {
  name: string;
  category: string;
  start_date: string;
  end_date: string;
  status: string;
  standings: Array<{
    team_name: string;
    played: number;
    wins: number;
    losses: number;
    draws: number;
    points: number;
  }>;
  upcoming_matches: Array<{
    round: number;
    date: string | null;
    time: string | null;
    home_team: string;
    away_team: string;
  }>;
}

interface Props {
  entity: Record<string, unknown>;
  onAction: (path: string) => void;
  brandColor?: string;
}

export default function LeagueLanding({ entity, onAction, brandColor }: Props) {
  const league = entity as unknown as LeagueEntity;
  const top5 = (league.standings ?? []).slice(0, 5);
  const upcoming = (league.upcoming_matches ?? []).slice(0, 5);

  const season = league.start_date && league.end_date
    ? `${format(parseISO(league.start_date), "d MMM", { locale: sv })} – ${format(parseISO(league.end_date), "d MMM yyyy", { locale: sv })}`
    : league.start_date
    ? `Från ${format(parseISO(league.start_date), "d MMM yyyy", { locale: sv })}`
    : null;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold">{league.name}</h1>
        <div className="flex flex-wrap gap-2">
          <Badge variant="secondary">{league.category}</Badge>
          {season && (
            <Badge variant="outline" className="gap-1">
              <Calendar className="h-3 w-3" />
              {season}
            </Badge>
          )}
        </div>
      </div>

      {/* Standings (top 5, server-computed) */}
      {top5.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="h-4 w-4" />
              Tabell
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="text-left px-4 py-2 font-medium">#</th>
                  <th className="text-left px-2 py-2 font-medium">Lag</th>
                  <th className="text-center px-2 py-2 font-medium">S</th>
                  <th className="text-center px-2 py-2 font-medium">V</th>
                  <th className="text-center px-2 py-2 font-medium">F</th>
                  <th className="text-center px-4 py-2 font-medium">P</th>
                </tr>
              </thead>
              <tbody>
                {top5.map((row, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="px-4 py-2 font-medium">{i + 1}</td>
                    <td className="px-2 py-2 truncate max-w-[140px]">{row.team_name}</td>
                    <td className="text-center px-2 py-2">{row.played}</td>
                    <td className="text-center px-2 py-2">{row.wins}</td>
                    <td className="text-center px-2 py-2">{row.losses}</td>
                    <td className="text-center px-4 py-2 font-semibold">{row.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Upcoming matches (server-filtered) */}
      {upcoming.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Kommande matcher</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {upcoming.map((m, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b last:border-0 pb-2 last:pb-0">
                <div className="flex-1 min-w-0">
                  <span className="text-muted-foreground mr-2">R{m.round}</span>
                  <span className="truncate">
                    {m.home_team} vs {m.away_team}
                  </span>
                </div>
                {m.date && (
                  <span className="text-xs text-muted-foreground shrink-0 ml-2">
                    {format(parseISO(m.date), "d/M", { locale: sv })}
                    {m.time ? ` ${m.time.slice(0, 5)}` : ""}
                  </span>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Action */}
      <Button
        className="w-full text-base py-6"
        style={brandColor ? { backgroundColor: brandColor } : undefined}
        disabled={league.status !== "active" && league.status !== "registration_open"}
        onClick={() => onAction("/leagues")}
      >
        Anmäl lag →
      </Button>
    </div>
  );
}
